import React, { useCallback, useMemo, useEffect } from 'react';
import { ResponseErrorPanel, TableColumn } from '@backstage/core-components';
import { CopyableText } from '../../CopyableText';
import { ModernResourceView } from '../../ModernResourceView';
import { AzureActionMenu, AzureActionConfirmationDialog } from '../actions';
import { VmssTableData } from './types';
import { mapResourcesToVmssTableData } from './utils';
import { ExecuteActionOptions } from '../../../api';
import {
  VmssResourceDetails,
  CRDAzureConfig,
} from '@internal/backstage-plugin-compute-resource-discovery-new-common';
import { useApi, errorApiRef, alertApiRef } from '@backstage/core-plugin-api';
import { useActionManagement } from './useActionManagement';
import { useAzureContext } from '../AzureContext';
import useAsyncRetry from 'react-use/lib/useAsyncRetry';

interface VmssTableProps {
  entityRef: string;
  azureConfig: CRDAzureConfig;
  title: string;
}

export const VmssTable: React.FC<VmssTableProps> = ({
  entityRef,
  azureConfig,
  title,
}) => {
  const { api, projectCode, refreshContext } = useAzureContext();

  const executeAction = useCallback(
    (options: ExecuteActionOptions) => {
      return api.executeAction(options);
    },
    [api],
  );

  const {
    value: resources,
    loading: isLoading,
    error: error,
    retry: refresh,
  } = useAsyncRetry(async () => {
    if (!projectCode) return [];
    return api.listResources({
      serviceType: 'azure',
      resourceType: 'vmss',
      projectCode: projectCode,
      entityRef: entityRef,
      cloudConfig: azureConfig,
    });
  }, [projectCode, api]);

  const handleRefresh = () => {
    // Invalidate cache for this specific Azure VMSS configuration
    api.invalidateCache('listResources');
    refresh();
  };

  // Move all hook calls before any conditional returns
  const errorApi = useApi(errorApiRef);
  const alertApi = useApi(alertApiRef);
  const {
    menuPosition,
    confirmationDialogOpen,
    selectedAction,
    isActionInProgress,
    handleTableActionClick,
    handleMenuClose,
    handleActionClick,
    handleConfirmAction,
    setConfirmationDialogOpen,
  } = useActionManagement({
    executeAction,
    refresh,
    errorApi,
    alertApi,
    azureConfig,
    entityRef,
    projectCode,
  });

  useEffect(() => {
    if (!refreshContext) return;

    const refreshKey = `azure:vmss:${azureConfig.resourceGroup}`;
    const unregister = refreshContext.registerRefresh(refreshKey, refresh);

    return unregister;
  }, [refreshContext, refresh, azureConfig.resourceGroup]);

  const columns = useMemo<TableColumn<VmssTableData>[]>(
    () => [
      {
        title: 'ID',
        field: 'identifier',
        render: (row: VmssTableData) => (
          <div style={{ maxWidth: '400px' }}>
            <CopyableText value={row.identifier} />
          </div>
        ),
      },
      {
        title: 'Name',
        field: 'name',
      },
      {
        title: 'Location',
        field: 'location',
      },
      {
        title: 'Resource Group',
        field: 'resourceGroup',
      },
      {
        title: 'SKU',
        field: 'sku',
      },
      {
        title: 'Capacity',
        field: 'capacity',
        type: 'numeric',
      },
      {
        title: 'Instance Count',
        field: 'instanceCount',
        type: 'numeric',
      },
      {
        title: 'Status',
        field: 'status',
      },
    ],
    [],
  );

  const data = useMemo(
    () =>
      resources
        ? mapResourcesToVmssTableData(resources as VmssResourceDetails[])
        : [],
    [resources],
  );

  if (error) {
    error.message = `Failed to load Azure VMSSs`;
    return <ResponseErrorPanel error={error} title={error.message} />;
  }

  return (
    <>
      <ModernResourceView<VmssTableData>
        title={title}
        data={data}
        isLoading={isLoading}
        onRefresh={handleRefresh}
        onActions={handleTableActionClick}
        cloudService="azure"
        resourceType="vmss"
        columns={columns}
        colorTheme="#0078D4" // Azure Blue
        emptyMessage="No VMSS instances found"
      />

      <AzureActionMenu
        handleActionClick={handleActionClick}
        handleMenuClose={handleMenuClose}
        handleOpenConnection={() => {}}
        menuPosition={menuPosition}
        service="vmss"
      />

      <AzureActionConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={handleConfirmAction}
        action={selectedAction as any}
        isLoading={isActionInProgress}
        service="vmss"
      />
    </>
  );
};
